import { useEffect, type RefObject } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { useInteraction } from '../chrome/interaction-store';
import { useToolStore, isPanMode } from '../chrome/tool-store';
import { wheelToViewport, pinchToViewport } from './viewport';

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

function setViewport(next: ReturnType<typeof wheelToViewport>) {
  useCanvasStore.setState((s) => ({ ...s, doc: { ...s.doc, viewport: next } }));
}

/**
 * Pan/zoom gestures for the infinite canvas, shared by legacy + unified render
 * paths. Design-tool semantics:
 *   - trackpad two-finger swipe → pan          (wheel, no ctrlKey)
 *   - trackpad pinch / ctrl|cmd+wheel → zoom    (smooth, cursor-anchored)
 *   - left-drag on empty background → pan
 *   - touchscreen two-finger → pan + pinch-zoom (pointer events)
 *
 * `el` must be the background element; mouse-pan only triggers when the press
 * lands on `el` itself (not a frame child), and shift-drag is left to marquee.
 */
export function useCanvasGestures(elRef: RefObject<HTMLElement | null>) {
  const dispatch = useCanvasStore((s) => s.dispatch);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    // Suppress hover highlight during wheel zoom; clear shortly after the last
    // wheel tick (wheel has no natural "end" event).
    let wheelIdle: ReturnType<typeof setTimeout> | undefined;

    // ---- trackpad / mouse wheel ----
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      useInteraction.getState().setPanZoom(true);
      clearTimeout(wheelIdle);
      wheelIdle = setTimeout(() => useInteraction.getState().setPanZoom(false), 150);
      const vp = useCanvasStore.getState().doc.viewport;
      setViewport(
        wheelToViewport(vp, {
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          deltaMode: e.deltaMode,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          cursor: { x: e.clientX, y: e.clientY },
        }),
      );
    };

    // ---- left-drag pan on empty background ----
    let panning = false;
    let lastX = 0;
    let lastY = 0;
    const onMouseDown = (e: MouseEvent) => {
      if (e.target !== el) return; // only the background, not a frame
      // Pan only with the Hand tool, while Space is held, or middle-button drag.
      // Otherwise (Select tool) left-drag belongs to the marquee.
      const pan = isPanMode() || e.button === 1;
      if (!pan) {
        // Select tool: a plain click on empty canvas clears the selection;
        // shift-drag is left to the (additive) marquee. Also drop any layer
        // selection so its overlay/action-bar doesn't linger after a click-off.
        if (!e.shiftKey && e.button === 0) {
          dispatch({ t: 'select', ids: [] });
          useCanvasStore.getState().setLayerSelection(null);
        }
        return;
      }
      panning = true;
      lastX = e.clientX;
      lastY = e.clientY;
      useInteraction.getState().setPanZoom(true);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!panning) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      const vp = useCanvasStore.getState().doc.viewport;
      setViewport({ zoom: vp.zoom, pan: { x: vp.pan.x + dx, y: vp.pan.y + dy } });
    };
    const onMouseUp = () => {
      if (panning) useInteraction.getState().setPanZoom(false);
      panning = false;
    };

    // ---- touchscreen two-finger pan + pinch ----
    const pointers = new Map<number, { x: number; y: number }>();
    let prevGesture: { center: { x: number; y: number }; distance: number } | null = null;

    const gestureOf = () => {
      const pts = [...pointers.values()];
      const center = {
        x: (pts[0].x + pts[1].x) / 2,
        y: (pts[0].y + pts[1].y) / 2,
      };
      const distance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      return { center, distance };
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return; // mouse uses the handlers above
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) prevGesture = gestureOf();
    };
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size !== 2 || !prevGesture) return;
      e.preventDefault();
      const next = gestureOf();
      const vp = useCanvasStore.getState().doc.viewport;
      setViewport(pinchToViewport(vp, prevGesture, next));
      prevGesture = next;
    };
    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      pointers.delete(e.pointerId);
      prevGesture = pointers.size === 2 ? gestureOf() : null;
    };

    // ---- Space = temporary Hand (pan) override, Figma-style ----
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || isTypingTarget(e.target)) return;
      e.preventDefault(); // stop page scroll
      if (!useToolStore.getState().spaceHeld) useToolStore.getState().setSpaceHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      useToolStore.getState().setSpaceHeld(false);
    };

    // Stop the browser hijacking pinch/swipe as page zoom/scroll.
    const prevTouchAction = el.style.touchAction;
    el.style.touchAction = 'none';

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);

    return () => {
      clearTimeout(wheelIdle);
      el.style.touchAction = prevTouchAction;
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [elRef, dispatch]);
}
